import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { downloadHistoryApi, type DownloadHistoryEntry } from 'services/downloadHistoryApi'
import { DownloadHistoryStatus } from '@numeo/types-main-web-app'

export enum DownloadStatus {
    DOWNLOADING = 'downloading',
    COMPLETED = 'completed',
    FAILED = 'failed',
}

export interface DownloadEntry {
    id: string
    filename: string
    status: DownloadStatus
    error?: string
    completedAt?: number
    /** Persistent server URL (GCS) – synced to DB, re-downloadable across sessions */
    url?: string
    /** In-memory blob URL – only available during the current session */
    blobUrl?: string
    /** 'file' for individual PDFs, 'folder' for bulk ZIPs */
    entryType?: 'file' | 'folder'
    /** GCS URLs of individual PDFs in a ZIP — enables re-download after page reload */
    fileUrls?: string[]
}

interface DownloadCenterContextValue {
    downloads: DownloadEntry[]
    addDownload: (id: string, filename: string, entryType?: 'file' | 'folder') => void
    /** For bulk ZIPs. Pass fileUrls (individual GCS URLs) to enable re-download across sessions. */
    completeDownload: (id: string, blob: Blob, fileUrls?: string[], filename?: string) => void
    /** For documents with a persistent server URL (individual PDFs stored in GCS). Re-downloadable across sessions. */
    completeDownloadWithUrl: (id: string, url: string, filename?: string) => void
    failDownload: (id: string, error: string) => void
    triggerDownload: (id: string) => Promise<void>
    clearAll: () => void
    isOpen: boolean
    setIsOpen: (open: boolean) => void
}

const noop = () => {}
const noopAsync = async () => {}

const defaultValue: DownloadCenterContextValue = {
    downloads: [],
    addDownload: noop,
    completeDownload: noop,
    completeDownloadWithUrl: noop,
    failDownload: noop,
    triggerDownload: noopAsync,
    clearAll: noop,
    isOpen: false,
    setIsOpen: noop,
}

const DownloadCenterContext = createContext<DownloadCenterContextValue>(defaultValue)

export function useDownloadCenter() {
    return useContext(DownloadCenterContext)
}

function fireAnchorDownload(href: string, filename: string) {
    const a = document.createElement('a')
    a.href = href
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
}

export function DownloadCenterProvider({ children }: { children: React.ReactNode }) {
    const [downloads, setDownloads] = useState<DownloadEntry[]>([])
    const [isOpen, setIsOpen] = useState(false)

    // Ref mirror of downloads so callbacks can read current state without stale closures
    const downloadsRef = useRef<DownloadEntry[]>([])
    downloadsRef.current = downloads

    // In-memory blob URLs – not persisted, keyed by download id
    const blobUrls = useRef<Record<string, string>>({})

    const mergeFromBackend = useCallback((entries: DownloadHistoryEntry[]) => {
        setDownloads((prev) => {
            // Keep in-progress entries and any completed in-memory blobs (ZIPs not yet/unable to persist to DB)
            const keepLocal = prev.filter((d) => d.status === DownloadStatus.DOWNLOADING || !!blobUrls.current[d.id])
            const keepIds = new Set(keepLocal.map((d) => d.id))
            const persisted: DownloadEntry[] = entries
                .filter((e) => !keepIds.has(e.downloadId))
                .map((e) => ({
                    id: e.downloadId,
                    filename: e.filename,
                    status: e.status === DownloadHistoryStatus.COMPLETED ? DownloadStatus.COMPLETED : DownloadStatus.FAILED,
                    error: e.error,
                    completedAt: new Date(e.completedAt).getTime(),
                    url: e.url,
                    fileUrls: e.fileUrls,
                    blobUrl: blobUrls.current[e.downloadId],
                    entryType: (e.filename.endsWith('.zip') ? 'folder' : 'file') as 'file' | 'folder',
                }))
            return [...keepLocal, ...persisted]
        })
    }, [])

    // Initial load on mount
    useEffect(() => {
        downloadHistoryApi
            .getAll()
            .then(mergeFromBackend)
            .catch((err) => console.error('[DownloadCenter] Failed to load history:', err))
    }, [mergeFromBackend])

    // Revoke blob URLs on unmount to avoid memory leaks
    useEffect(() => {
        return () => {
            Object.values(blobUrls.current).forEach((u) => URL.revokeObjectURL(u))
        }
    }, [])

    const addDownload = useCallback((id: string, filename: string, entryType?: 'file' | 'folder') => {
        setDownloads((prev) => {
            const entry: DownloadEntry = { id, filename, status: DownloadStatus.DOWNLOADING, entryType }
            return prev.some((d) => d.id === id) ? prev.map((d) => (d.id === id ? entry : d)) : [entry, ...prev]
        })
    }, [])

    const completeDownload = useCallback((id: string, blob: Blob, fileUrls?: string[], filename?: string) => {
        if (blobUrls.current[id]) URL.revokeObjectURL(blobUrls.current[id])
        const blobUrl = URL.createObjectURL(blob)
        blobUrls.current[id] = blobUrl
        const completedAt = Date.now()
        // Read current filename from ref before updating state; a server-provided name replaces the placeholder.
        const existing = downloadsRef.current.find((d) => d.id === id)
        const name = filename ?? existing?.filename ?? id
        setDownloads((prev) =>
            prev.map((d) =>
                d.id === id ? { ...d, filename: name, status: DownloadStatus.COMPLETED, completedAt, blobUrl, entryType: 'folder' as const, fileUrls } : d
            )
        )
        // Sync to DB — store fileUrls so the ZIP can be re-built after reload
        downloadHistoryApi
            .upsert({ downloadId: id, filename: name, status: DownloadHistoryStatus.COMPLETED, completedAt: new Date(completedAt).toISOString(), fileUrls })
            .catch((err) => console.error('[DownloadCenter] Failed to save ZIP download:', err))
    }, [])

    const completeDownloadWithUrl = useCallback((id: string, url: string, filename?: string) => {
        const completedAt = Date.now()
        // Read current filename from ref before updating state
        const existing = downloadsRef.current.find((d) => d.id === id)
        const name = filename ?? existing?.filename ?? id
        setDownloads((prev) => {
            const entry = prev.find((d) => d.id === id)
            return entry
                ? prev.map((d) => (d.id === id ? { ...d, status: DownloadStatus.COMPLETED, completedAt, url, entryType: 'file' as const } : d))
                : [{ id, filename: name, status: DownloadStatus.COMPLETED, completedAt, url, entryType: 'file' as const }, ...prev]
        })
        // Sync to backend outside setState to avoid double-calls in React strict mode
        downloadHistoryApi
            .upsert({ downloadId: id, filename: name, status: DownloadHistoryStatus.COMPLETED, completedAt: new Date(completedAt).toISOString(), url })
            .catch((err) => console.error('[DownloadCenter] Failed to save download:', err))
    }, [])

    const failDownload = useCallback((id: string, error: string) => {
        const completedAt = Date.now()
        const existing = downloadsRef.current.find((d) => d.id === id)
        const name = existing?.filename ?? id
        setDownloads((prev) => prev.map((d) => (d.id === id ? { ...d, status: DownloadStatus.FAILED, error, completedAt } : d)))
        downloadHistoryApi
            .upsert({ downloadId: id, filename: name, status: DownloadHistoryStatus.FAILED, error, completedAt: new Date(completedAt).toISOString() })
            .catch((err) => console.error('[DownloadCenter] Failed to save failed download:', err))
    }, [])

    const triggerDownload = useCallback(
        async (id: string) => {
            const entry = downloadsRef.current.find((d) => d.id === id)
            if (!entry) return

            // In-memory blob (same session)
            const blobUrl = blobUrls.current[id]
            if (blobUrl) {
                fireAnchorDownload(blobUrl, entry.filename)
                return
            }

            // Persistent server URL (re-download single PDF from GCS across sessions)
            if (entry.url) {
                try {
                    const blob = await fetch(entry.url).then((r) => {
                        if (!r.ok) throw new Error(`HTTP ${r.status}`)
                        return r.blob()
                    })
                    const tempUrl = URL.createObjectURL(blob)
                    fireAnchorDownload(tempUrl, entry.filename)
                    URL.revokeObjectURL(tempUrl)
                } catch (err) {
                    console.error('[DownloadCenter] Failed to re-download file:', err)
                    setDownloads((prev) =>
                        prev.map((d) =>
                            d.id === id ? { ...d, status: DownloadStatus.FAILED, error: 'File could not be downloaded. The link may have expired.' } : d
                        )
                    )
                }
                return
            }

            // Re-zip from individual GCS URLs (bulk ZIP re-download across sessions)
            if (entry.fileUrls && entry.fileUrls.length > 0) {
                try {
                    const { default: JSZip } = await import('jszip')
                    const zip = new JSZip()
                    await Promise.all(
                        entry.fileUrls.map(async (url, i) => {
                            const r = await fetch(url)
                            if (!r.ok) throw new Error(`HTTP ${r.status} fetching PDF ${i + 1}`)
                            const buf = await r.arrayBuffer()
                            const name = url.split('/').pop()?.split('?')[0] ?? `statement-${i + 1}.pdf`
                            zip.file(name, buf)
                        })
                    )
                    const zipBlob = await zip.generateAsync({ type: 'blob' })
                    const tempUrl = URL.createObjectURL(zipBlob)
                    fireAnchorDownload(tempUrl, entry.filename)
                    URL.revokeObjectURL(tempUrl)
                } catch (err) {
                    console.error('[DownloadCenter] Failed to re-zip files:', err)
                    setDownloads((prev) =>
                        prev.map((d) =>
                            d.id === id ? { ...d, status: DownloadStatus.FAILED, error: 'ZIP could not be rebuilt. Some files may have expired.' } : d
                        )
                    )
                }
            }
        },
        [] // uses downloadsRef, no stale closure risk
    )

    const clearAll = useCallback(() => {
        Object.values(blobUrls.current).forEach((u) => URL.revokeObjectURL(u))
        blobUrls.current = {}
        setDownloads([])
        downloadHistoryApi.clearAll().catch((err) => console.error('[DownloadCenter] Failed to clear history:', err))
    }, [])

    const openModal = useCallback(
        (open: boolean) => {
            setIsOpen(open)
            // Re-sync from DB whenever the modal is opened so other browsers/tabs are reflected
            if (open) {
                downloadHistoryApi
                    .getAll()
                    .then(mergeFromBackend)
                    .catch((err) => console.error('[DownloadCenter] Failed to refresh history:', err))
            }
        },
        [mergeFromBackend]
    )

    return (
        <DownloadCenterContext.Provider
            value={{
                downloads,
                addDownload,
                completeDownload,
                completeDownloadWithUrl,
                failDownload,
                triggerDownload,
                clearAll,
                isOpen,
                setIsOpen: openModal,
            }}
        >
            {children}
        </DownloadCenterContext.Provider>
    )
}
